// ./HarvestUpdateModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Modal,
  DatePicker,
  InputNumber,
  Dropdown,
  Typography,
  Button,
  message,
} from "antd";
import { DownOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

import { COLORS, UI_DATE_FORMAT } from "../../../consts/index.js"; // keep consistent with your app
import { markCustom } from "./harvestUpdate.service.js";

const { Text } = Typography;

export default function HarvestUpdateModal({
  open,
  onClose,
  onSaved,
  siteMetadata,
  orgId,
  siteId,
  cropId,
  targets = [], // array of { greenhouseName, rowLabel } (unharvested-only)
}) {
  // Titles and config from metadata (same keys as Forecast page)
  const numberOfTubPerCrate =
    siteMetadata?.NUMBER_OF_TUBS_PER_CRATE != null
      ? Number(siteMetadata.NUMBER_OF_TUBS_PER_CRATE)
      : 36;

  const crateTableHeaderName =
    siteMetadata?.BULK_UPDATE_HARVEST_DATE_CRATE_TABLE_HEADER_NAME ||
    "# Crates";

  const tubTableHeaderName =
    siteMetadata?.BULK_UPDATE_HARVEST_DATE_TUB_TABLE_HEADER_NAME || "# Tubs";

  const showWeight =
    !!siteMetadata?.FORECAST_GREENHOUSE_TABLE?.SHOW_FORECAST_WEIGHT_COLUMN;

  const weightTitle =
    siteMetadata?.FORECAST_GREENHOUSE_TABLE
      ?.FORECAST_WEIGHT_KEY_NAME_FOR_TABLE || "Weight";

  const [saving, setSaving] = useState(false);
  const [date, setDate] = useState(dayjs());
  const [palletStatus, setPalletStatus] = useState("normal"); // "normal" | "unavailable" | "discarded"
  const [crates, setCrates] = useState(undefined); // default undefined (Normal)
  const [tubs, setTubs] = useState(undefined); // default undefined (Normal)
  const [weight, setWeight] = useState(undefined); // optional

  // Reset when opened/closed
  useEffect(() => {
    if (!open) return;
    setDate(dayjs());
    setPalletStatus("normal");
    setCrates(undefined);
    setTubs(undefined);
    setWeight(undefined);
  }, [open]);

  const isDateSelected = !!date && dayjs(date).isValid();

  // Menu like BulkHarvestModal (Forecast): when no date, only Discarded is allowed to be set quickly
  const crateMenu = useMemo(() => {
    const itemsWhenDated = [
      {
        key: "unavailable",
        label: "Unavailable",
        onClick: () => handleQuickStatus("unavailable"),
      },
      {
        key: "discarded",
        label: "Discarded",
        onClick: () => handleQuickStatus("discarded"),
      },
      {
        key: "add",
        label: "Add Pallets",
        onClick: () => handleQuickAddPallet(),
      },
    ];
    const itemsWhenNoDate = [
      {
        key: "discarded",
        label: "Discarded",
        onClick: () => handleQuickStatus("discarded"),
      },
    ];
    return { items: isDateSelected ? itemsWhenDated : itemsWhenNoDate };
  }, [isDateSelected, crates, tubs]);

  function handleQuickStatus(next) {
    if (next === "discarded") {
      setPalletStatus("discarded");
      setCrates(undefined);
      setTubs(undefined);
      return;
    }
    if (!isDateSelected) {
      // Mirror Forecast behavior: without a date, only "Discarded" is allowed via quick set
      return;
    }
    if (next === "unavailable") {
      setPalletStatus("unavailable");
      setCrates(undefined);
      setTubs(undefined);
      return;
    }
    // fallback: keep normal
    setPalletStatus("normal");
  }

  function handleQuickAddPallet() {
    if (!isDateSelected) return;
    setPalletStatus("normal");
    setCrates((prev) => (typeof prev === "number" && prev > 0 ? prev : 1));
  }

  async function handleSave() {
    // Validation same spirit as Forecast page:
    // - If not Discarded, date is required.
    // - If Discarded and no date, we’ll use today.
    const usingDate =
      palletStatus === "discarded" ? (isDateSelected ? date : dayjs()) : date;

    if (palletStatus !== "discarded" && !isDateSelected) {
      message.error(
        "Harvest date is required unless you mark it as Discarded.",
      );
      return;
    }

    // Coerce values per rules:
    // - Normal: crates can be undefined in UI, but becomes 0 when saving.
    // - Unavailable: crates = 0, tubs ignored.
    // - Discarded: crates = -1, tubs ignored.
    let cratesOut;
    let tubsOut = 0;

    if (palletStatus === "discarded") {
      cratesOut = -1;
      tubsOut = 0;
    } else if (palletStatus === "unavailable") {
      cratesOut = 0;
      tubsOut = 0;
    } else {
      // normal
      cratesOut = typeof crates === "number" ? crates : 0; // undefined/null -> 0 for save
      tubsOut = typeof tubs === "number" ? tubs : 0;
    }

    const weightOut =
      showWeight && typeof weight === "number" ? weight : undefined;

    setSaving(true);
    try {
      const { failed, total } = await markCustom({
        date: usingDate, // dayjs instance, your service converts to backend format
        crates: cratesOut,
        tubs: tubsOut,
        weight: weightOut,
        palletStatus, // "normal" | "unavailable" | "discarded"
        tubsPerCrate: numberOfTubPerCrate,
        targets, // apply only to unharvested targets
        org: orgId,
        siteId,
        cropId,
      });

      if (failed === 0) {
        message.success(`Updated ${total} ${total === 1 ? "row" : "rows"}.`);
        onSaved?.();
      } else {
        message.error(`Updated ${total - failed}, failed ${failed}.`);
      }
    } catch (e) {
      console.error(e);
      message.error(e?.message || "Unexpected error while saving.");
    } finally {
      setSaving(false);
    }
  }

  // Derived visual helpers (minimal; no color chips to keep it light and dependency-free)
  const statusBadgeText =
    palletStatus === "discarded"
      ? "Discarded"
      : palletStatus === "unavailable"
        ? "Unavailable"
        : "Normal";

  return (
    <Modal
      open={open}
      title="Update Harvested Status"
      onCancel={onClose}
      onOk={handleSave}
      okText="Save"
      confirmLoading={saving}
      width={560}
      centered
      maskClosable={false}
      destroyOnHidden
    >
      {/* Date */}
      <div className="flex items-center gap-4 mb-5">
        <div style={{ width: 140 }}>
          <Text strong>Harvest Date</Text>
        </div>
        <DatePicker
          value={date}
          onChange={setDate}
          format={UI_DATE_FORMAT || "DD MMM, YYYY"}
          style={{ width: 240 }}
        />
      </div>

      {/* Crates with status dropdown (title from metadata) */}
      <div className="flex items-center gap-4 mb-5">
        <div style={{ width: 140 }}>
          <Text strong>{crateTableHeaderName}</Text>
        </div>

        {palletStatus === "normal" ? (
          <InputNumber
            min={0}
            value={crates}
            onChange={(v) => setCrates(typeof v === "number" ? v : undefined)}
            disabled={!isDateSelected}
            style={{ width: 240 }}
            placeholder={crateTableHeaderName}
          />
        ) : (
          <div
            className="px-3 py-1.5 rounded-md border"
            style={{ minWidth: 240, textAlign: "center" }}
          >
            <Text>{statusBadgeText}</Text>
          </div>
        )}

        <Dropdown menu={crateMenu} trigger={["click"]}>
          <Button icon={<DownOutlined />} />
        </Dropdown>
      </div>

      {/* Tubs (title from metadata) */}
      <div className="flex items-center gap-4 mb-5">
        <div style={{ width: 140 }}>
          <Text strong>{tubTableHeaderName}</Text>
        </div>
        {palletStatus === "normal" && isDateSelected ? (
          <InputNumber
            min={0}
            value={tubs}
            onChange={(v) => setTubs(typeof v === "number" ? v : undefined)}
            style={{ width: 240 }}
            placeholder={tubTableHeaderName}
          />
        ) : (
          <div
            className="px-3 py-1.5 rounded-md border"
            style={{ minWidth: 240, textAlign: "center" }}
          >
            <Text type="secondary">N/A</Text>
          </div>
        )}
      </div>

      {/* Optional weight column parity with Forecast config */}
      {showWeight && (
        <div className="flex items-center gap-4 mb-5">
          <div style={{ width: 140 }}>
            <Text strong>{weightTitle}</Text>
          </div>
          <InputNumber
            min={0}
            step={1}
            value={weight}
            onChange={(v) => setWeight(typeof v === "number" ? v : undefined)}
            style={{ width: 240 }}
            placeholder={weightTitle}
            disabled={palletStatus === "discarded"}
          />
        </div>
      )}

      <div
        className="mt-2 text-xs"
        style={{ color: COLORS.darkerGray }}
      >
        Applying to {targets?.length || 0} unharvested row
        {(targets?.length || 0) === 1 ? "" : "s"}.
      </div>
    </Modal>
  );
}
